import React, { useState, useEffect, useCallback } from 'react';
import { getCognitiveTracker } from '../../../services/cognitiveSkills';
import { getModuleInstance } from '../../../data/moduleInstances';
import './style.css';

// Generic Adaptive Module Component
// Handles instance selection and scoring for modules 2, 3, 5, 6, and 7
const AdaptiveModule = ({ 
  moduleId, 
  onComplete, 
  renderContent,
  onAnswer,
  initialData 
}) => {
  const [tracker] = useState(() => getCognitiveTracker());
  const [currentInstance, setCurrentInstance] = useState(null);
  const [instanceCount, setInstanceCount] = useState(0);
  const [moduleState, setModuleState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Initialize module on mount
  useEffect(() => {
    const state = tracker.startModule(moduleId);
    setModuleState(state);
    
    // Load first instance (difficulty 5)
    const firstInstance = getModuleInstance(moduleId, state?.currentDifficulty || 5);
    setCurrentInstance(firstInstance);
    setInstanceCount(1);
  }, [moduleId, tracker]);

  // Handle answer submission
  const handleAnswer = useCallback((isCorrect, additionalData = {}) => {
    if (!currentInstance || !moduleState) return;

    setIsLoading(true);
    
    console.log(`🎯 AdaptiveModule handleAnswer:`, {
      moduleId,
      isCorrect,
      currentDifficulty: currentInstance.difficulty,
      instanceCount,
      additionalData
    });
    
    // Record answer and get next difficulty
    const result = tracker.recordAnswer(
      moduleId, 
      isCorrect, 
      currentInstance.difficulty,
      currentInstance.instanceId,
      additionalData.customPoints
    );
    
    console.log(`🎯 AdaptiveModule tracker result:`, result);

    // Show feedback
    setFeedback({
      isCorrect,
      message: isCorrect ? 'Correct!' : 'Incorrect',
      points: additionalData.customPoints !== undefined ? additionalData.customPoints : (isCorrect ? currentInstance.difficulty : 0)
    });

    // Callback to parent if provided
    if (onAnswer) {
      onAnswer(isCorrect, {
        ...additionalData,
        difficulty: currentInstance.difficulty,
        instanceCount: instanceCount
      });
    }

    // Proceed to next instance or complete module
    setTimeout(() => {
      setFeedback(null);
      
      if (result && result.instancesRemaining > 0) {
        // Load next instance
        console.log(`🎯 Loading next instance: difficulty=${result.nextDifficulty}, remaining=${result.instancesRemaining}`);
        const nextInstance = getModuleInstance(moduleId, result.nextDifficulty);
        console.log(`🎯 Next instance loaded:`, nextInstance);
        setCurrentInstance(nextInstance);
        setInstanceCount(instanceCount + 1);
        setModuleState({
          ...moduleState,
          currentDifficulty: result.nextDifficulty
        });
        // Scroll to top for new instance
        window.scrollTo(0, 0);
      } else {
        // Module complete
        const percentiles = tracker.calculatePercentiles();
        onComplete({
          moduleId,
          percentiles,
          totalInstances: instanceCount,
          finalState: tracker.getState()
        });
      }
      
      setIsLoading(false);
    }, 1500); // Show feedback for 1.5 seconds
  }, [currentInstance, moduleState, moduleId, instanceCount, tracker, onAnswer, onComplete]);

  // Progress indicator
  const progress = instanceCount / 5; // 5 instances per adaptive module

  if (!currentInstance) {
    return <div className="adaptive-module-loading">Loading module...</div>;
  }

  return (
    <div className="adaptive-module">
      <div className="adaptive-module-header">
        <h3>{currentInstance.title}</h3>
        <div className="difficulty-indicator">
          <span>Difficulty: {currentInstance.difficulty}/10</span>
          <div className="difficulty-bar">
            <div 
              className="difficulty-fill" 
              style={{ width: `${currentInstance.difficulty * 10}%` }}
            />
          </div>
        </div>
        <div className="instance-counter">
          Instance {instanceCount} of 5
        </div>
      </div>

      <div className="adaptive-module-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      </div>

      {feedback && (
        <div className={`feedback-overlay ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
          <div className="feedback-content">
            <h2>{feedback.message}</h2>
            {feedback.isCorrect && (
              <p>+{feedback.points} points</p>
            )}
          </div>
        </div>
      )}

      <div className={`adaptive-module-content ${isLoading ? 'loading' : ''}`}>
        {renderContent({
          instance: currentInstance,
          onSubmit: handleAnswer,
          instanceNumber: instanceCount,
          moduleId
        })}
      </div>
    </div>
  );
};

export default AdaptiveModule;